import { useState, type ReactNode } from 'react';
import { Alert, Button, Select, Skeleton, Table, Tooltip, Typography } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import type {
  FacturacionPorDiaCliente,
  FacturacionPorDiaFactura,
  FacturacionPorDiaPeriodo,
} from '@aid/shared';
import { DashboardCard } from '../../../components/DashboardCard';
import { EmptyState } from '../../../components/EmptyState';
import { StatusTag, type StatusTagTone } from '../../../components/StatusTag';
import { parseDateOnly, DATE_FORMAT } from '../../../lib/date';
import { formatCurrency } from '../../../lib/format';
import { i18nInstance, useT } from '../../../locales';
import { todayInTimeZone } from '../lib/ruta-cobros';
import { useFacturacionPorDia } from '../api/useFacturacionPorDia';
import './facturacion-por-dia.css';

const { Text } = Typography;
const BILLING_DAYS = Array.from({ length: 31 }, (_, index) => index + 1);

const COVERAGE_STATE = {
  COVERED: 'covered',
  HISTORICAL: 'historical',
  MISSING: 'missing',
} as const;

type CoverageState = (typeof COVERAGE_STATE)[keyof typeof COVERAGE_STATE];
type FacturacionPorDiaTranslate = ReturnType<typeof useT>;

interface FacturacionPorDiaProps {
  /** Optional default keeps the component deterministic for focused tests. */
  defaultDay?: number;
}

interface InvoiceStateCellProps {
  record: FacturacionPorDiaCliente;
  t: FacturacionPorDiaTranslate;
}

interface InvoiceAmountsCellProps {
  invoice: FacturacionPorDiaFactura | null;
  t: FacturacionPorDiaTranslate;
}

interface ApplicationsCellProps {
  invoice: FacturacionPorDiaFactura | null;
  language: string;
  t: FacturacionPorDiaTranslate;
}

interface CoverageIndicatorProps {
  periods: FacturacionPorDiaPeriodo[];
  language: string;
  t: FacturacionPorDiaTranslate;
}

function validDay(value: number | undefined): value is number {
  return value !== undefined && Number.isInteger(value) && value >= 1 && value <= 31;
}

function defaultBillingDay(): number {
  const day = Number(todayInTimeZone().slice(-2));
  return validDay(day) ? day : 1;
}

function formatEffectiveDate(value: string | null | undefined): string | null {
  return parseDateOnly(value)?.format(DATE_FORMAT) ?? null;
}

function formatPeriod(
  start: string | null | undefined,
  end: string | null | undefined,
  t: FacturacionPorDiaTranslate,
): string {
  const startLabel = formatEffectiveDate(start);
  const endLabel = formatEffectiveDate(end);
  return startLabel && endLabel
    ? t('dashboard.facturacionPorDia.periodRange', { start: startLabel, end: endLabel })
    : t('common.notAvailable');
}

function formatCoverageMonth(value: string | null | undefined, language: string): string {
  const date = parseDateOnly(value);
  if (!date) return '—';

  const locale = language.toLowerCase().startsWith('en') ? 'en' : 'es';
  const month = date.locale(locale).format('MMM').replace(/\./g, '');
  return month.charAt(0).toUpperCase() + month.slice(1);
}

function formatPaymentInstant(
  value: Date | string | null | undefined,
  language: string,
): string | null {
  if (value === null || value === undefined || value === '') return null;
  const instant = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(instant.getTime())) return null;

  try {
    return new Intl.DateTimeFormat(language.startsWith('en') ? 'en-US' : 'es-GT', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(instant);
  } catch {
    return instant.toLocaleString();
  }
}

function invoiceStatusLabel(
  invoice: FacturacionPorDiaFactura,
  t: FacturacionPorDiaTranslate,
): string {
  const statusKey = invoice.estadoKey?.toUpperCase();
  if (statusKey === 'PAGADA') return t('dashboard.facturacionPorDia.invoiceStatusPaid');
  if (statusKey === 'VIGENTE') return t('dashboard.facturacionPorDia.invoiceStatusCurrent');
  if (statusKey === 'ANULADA' || statusKey === 'ANULADO') {
    return t('dashboard.facturacionPorDia.invoiceStatusAnnulled');
  }
  return invoice.estadoValue?.trim() || t('dashboard.facturacionPorDia.invoiceStatusUnknown');
}

function invoiceStatusTone(invoice: FacturacionPorDiaFactura): StatusTagTone {
  if (!invoice.vigente) return 'error';
  if (invoice.estadoKey?.toUpperCase() === 'PAGADA') return 'success';
  if (invoice.estadoKey?.toUpperCase() === 'VIGENTE') return 'info';
  return 'neutral';
}

function InvoiceStateCell({ record, t }: InvoiceStateCellProps) {
  const invoice = record.ultimaFacturaGenerada;
  const futureInvoiceGenerated = record.facturaSiguienteGenerada;
  const stateLabel = !invoice
    ? t('dashboard.facturacionPorDia.invoicePending')
    : invoice?.vigente
      ? t('dashboard.facturacionPorDia.invoiceGenerated')
      : t('dashboard.facturacionPorDia.invoiceHistorical');
  const stateTone: StatusTagTone = !invoice ? 'warning' : invoice.vigente ? 'success' : 'error';
  const stateIcon = !invoice ? (
    <ClockCircleOutlined aria-hidden="true" />
  ) : invoice?.vigente ? (
    <CheckCircleOutlined aria-hidden="true" />
  ) : (
    <ExclamationCircleOutlined aria-hidden="true" />
  );
  const hasCoverage = record.siguientePeriodo.cubierta;
  const coverageStatusLabel = hasCoverage
    ? t('dashboard.facturacionPorDia.coverageCovered')
    : t('dashboard.facturacionPorDia.coverageNotCovered');
  const coverageStatusTone: StatusTagTone = hasCoverage ? 'success' : 'error';

  return (
    <div className="facturacion-por-dia__invoice" data-testid={`ultima-factura-${record.id}`}>
      <div className="facturacion-por-dia__tag-row">
        <StatusTag tone={stateTone}>
          {stateIcon} {stateLabel}
        </StatusTag>
        {invoice && (
          <StatusTag tone={invoiceStatusTone(invoice)}>{invoiceStatusLabel(invoice, t)}</StatusTag>
        )}
        {futureInvoiceGenerated && (
          <StatusTag tone={coverageStatusTone}>
            {hasCoverage ? (
              <CheckCircleOutlined aria-hidden="true" />
            ) : (
              <ExclamationCircleOutlined aria-hidden="true" />
            )}{' '}
            {coverageStatusLabel}
          </StatusTag>
        )}
      </div>
      {invoice && (
        <>
          <Text strong className="facturacion-por-dia__invoice-id">
            {t('dashboard.facturacionPorDia.invoiceId', { id: invoice.id })}
          </Text>
          <Text type="secondary" className="facturacion-por-dia__invoice-status">
            {t('dashboard.facturacionPorDia.invoicePeriodField', {
              period: formatPeriod(invoice.fechaInicio, invoice.fechaFin, t),
            })}
          </Text>
          <Text type="secondary" className="facturacion-por-dia__invoice-status">
            {t('dashboard.facturacionPorDia.invoiceStatusField', {
              status: invoiceStatusLabel(invoice, t),
            })}
          </Text>
          {!invoice.vigente && (
            <Text type="secondary" className="facturacion-por-dia__invoice-warning">
              {t('dashboard.facturacionPorDia.invoiceNotCovered')}
            </Text>
          )}
        </>
      )}
      {futureInvoiceGenerated && record.siguientePeriodo.facturasEncontradas > 1 && (
        <Text type="secondary" className="facturacion-por-dia__invoice-warning">
          {t('dashboard.facturacionPorDia.invoiceMultipleRecords', {
            count: record.siguientePeriodo.facturasEncontradas,
          })}
        </Text>
      )}
    </div>
  );
}

function InvoiceAmountsCell({ invoice, t }: InvoiceAmountsCellProps) {
  if (!invoice) {
    return <Text type="secondary">{t('common.notAvailable')}</Text>;
  }

  const outstandingIsZero = Number(invoice.saldoPendiente) === 0;
  return (
    <div
      className="facturacion-por-dia__amounts"
      aria-label={t('dashboard.facturacionPorDia.amounts')}
    >
      <div className="facturacion-por-dia__amount">
        <Text type="secondary">{t('dashboard.facturacionPorDia.gross')}</Text>
        <Text className="facturacion-por-dia__money">{formatCurrency(invoice.gross)}</Text>
      </div>
      <div className="facturacion-por-dia__amount">
        <Text type="secondary">{t('dashboard.facturacionPorDia.amountApplied')}</Text>
        <Text className="facturacion-por-dia__money">{formatCurrency(invoice.montoAplicado)}</Text>
      </div>
      <div className="facturacion-por-dia__amount">
        <Text type="secondary">{t('dashboard.facturacionPorDia.outstanding')}</Text>
        <Text
          className={`facturacion-por-dia__money${outstandingIsZero ? ' facturacion-por-dia__money--settled' : ' facturacion-por-dia__money--outstanding'}`}
        >
          {formatCurrency(invoice.saldoPendiente)}
        </Text>
      </div>
    </div>
  );
}

function applicationReference(
  application: FacturacionPorDiaFactura['aplicaciones'][number],
  t: FacturacionPorDiaTranslate,
): string {
  const references = [
    application.idPago !== null
      ? t('dashboard.facturacionPorDia.paymentReference', { id: application.idPago })
      : null,
    application.idCredito !== null
      ? t('dashboard.facturacionPorDia.creditReference', { id: application.idCredito })
      : null,
  ].filter((reference): reference is string => reference !== null);

  return references.length > 0
    ? references.join(' · ')
    : t('dashboard.facturacionPorDia.applicationNoReference');
}

function ApplicationsCell({ invoice, language, t }: ApplicationsCellProps) {
  if (!invoice) {
    return <Text type="secondary">{t('common.notAvailable')}</Text>;
  }

  if (invoice.aplicaciones.length === 0) {
    return <Text type="secondary">{t('dashboard.facturacionPorDia.noApplications')}</Text>;
  }

  return (
    <div
      className="facturacion-por-dia__applications"
      role="list"
      aria-label={t('dashboard.facturacionPorDia.applicationsLabel')}
    >
      {invoice.aplicaciones.map((application) => {
        const reference = applicationReference(application, t);
        const paymentDate = formatPaymentInstant(application.fechaPago, language);
        const dateLabel = paymentDate
          ? t('dashboard.facturacionPorDia.paymentDate', { date: paymentDate })
          : application.idCredito !== null
            ? t('dashboard.facturacionPorDia.creditConsumption')
            : t('dashboard.facturacionPorDia.paymentDateUnavailable');
        const details = t('dashboard.facturacionPorDia.applicationTooltip', {
          id: application.id,
          source: reference,
          amount: formatCurrency(application.montoAplicado),
          date: dateLabel,
        });

        return (
          <Tooltip title={details} key={application.id}>
            <div
              className="facturacion-por-dia__application"
              role="listitem"
              tabIndex={0}
              aria-label={details}
            >
              <div className="facturacion-por-dia__application-source">
                <Text strong>
                  {t('dashboard.facturacionPorDia.applicationId', { id: application.id })}
                </Text>
                <Text type="secondary">{reference}</Text>
                <Text type="secondary">{dateLabel}</Text>
              </div>
              <Text className="facturacion-por-dia__money">
                {formatCurrency(application.montoAplicado)}
              </Text>
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
}

function coverageState(period: FacturacionPorDiaPeriodo): CoverageState {
  if (period.cubierta) return COVERAGE_STATE.COVERED;
  return period.facturasEncontradas > 0 ? COVERAGE_STATE.HISTORICAL : COVERAGE_STATE.MISSING;
}

function coverageLabel(state: CoverageState, t: FacturacionPorDiaTranslate): string {
  if (state === COVERAGE_STATE.COVERED) return t('dashboard.facturacionPorDia.coverageCovered');
  if (state === COVERAGE_STATE.HISTORICAL) {
    return t('dashboard.facturacionPorDia.coverageHistorical');
  }
  return t('dashboard.facturacionPorDia.coverageMissing');
}

function coverageIcon(state: CoverageState): ReactNode {
  if (state === COVERAGE_STATE.COVERED) return <CheckCircleOutlined aria-hidden="true" />;
  if (state === COVERAGE_STATE.HISTORICAL) {
    return <ExclamationCircleOutlined aria-hidden="true" />;
  }
  return <ClockCircleOutlined aria-hidden="true" />;
}

function coverageDetail(
  period: FacturacionPorDiaPeriodo,
  state: CoverageState,
  t: FacturacionPorDiaTranslate,
): string {
  if (state === COVERAGE_STATE.COVERED && period.facturasVivas > 1) {
    return t('dashboard.facturacionPorDia.coverageDuplicateDetail', {
      count: period.facturasVivas,
    });
  }
  if (state === COVERAGE_STATE.COVERED && period.facturasEncontradas > 1) {
    return t('dashboard.facturacionPorDia.coverageDuplicateHistoryDetail', {
      count: period.facturasEncontradas,
    });
  }
  if (state === COVERAGE_STATE.COVERED) {
    return t('dashboard.facturacionPorDia.coverageLiveDetail');
  }
  if (state === COVERAGE_STATE.HISTORICAL) {
    return t('dashboard.facturacionPorDia.coverageHistoricalDetail', {
      count: period.facturasEncontradas,
    });
  }
  return t('dashboard.facturacionPorDia.coverageMissingDetail');
}

function CoverageIndicator({ periods, language, t }: CoverageIndicatorProps) {
  return (
    <div
      className="facturacion-por-dia__coverage"
      role="list"
      aria-label={t('dashboard.facturacionPorDia.recentCoverageLabel')}
    >
      {periods.slice(0, 12).map((period, index) => {
        const state = coverageState(period);
        const label = coverageLabel(state, t);
        const details = t('dashboard.facturacionPorDia.coverageTooltip', {
          period: formatPeriod(period.fechaInicio, period.fechaFin, t),
          status: label,
          detail: coverageDetail(period, state, t),
        });
        const month = formatCoverageMonth(period.fechaInicio, language);

        return (
          <Tooltip title={details} key={`${period.fechaInicio}-${index}`}>
            <span
              className="facturacion-por-dia__coverage-item"
              role="listitem"
              tabIndex={0}
              aria-label={`${month}: ${details}`}
              data-testid={`facturacion-por-dia-coverage-period-${index + 1}`}
            >
              <span className="facturacion-por-dia__coverage-month" aria-hidden="true">
                {month}
              </span>
              <span
                className={`facturacion-por-dia__coverage-cell facturacion-por-dia__coverage-cell--${state}`}
                aria-hidden="true"
              >
                {coverageIcon(state)}
              </span>
            </span>
          </Tooltip>
        );
      })}
    </div>
  );
}

export function FacturacionPorDia({ defaultDay }: FacturacionPorDiaProps = {}) {
  const t = useT();
  const [selectedDay, setSelectedDay] = useState(
    validDay(defaultDay) ? defaultDay : defaultBillingDay(),
  );
  const query = useFacturacionPorDia(selectedDay);
  const response = query.data;
  const entries = response?.data ?? [];
  const initialLoading = query.isLoading && !response;
  const language = i18nInstance.language;

  const columns: ColumnsType<FacturacionPorDiaCliente> = [
    {
      title: t('dashboard.facturacionPorDia.index'),
      key: 'index',
      width: 56,
      render: (_value: unknown, _record: FacturacionPorDiaCliente, index: number) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {index + 1}
        </Text>
      ),
    },
    {
      title: t('dashboard.facturacionPorDia.client'),
      dataIndex: 'nombre',
      key: 'nombre',
      width: 220,
      render: (value: string, record: FacturacionPorDiaCliente) => {
        const name =
          value.trim() || t('dashboard.facturacionPorDia.clientFallback', { id: record.id });
        const identifiers = [record.ip, record.identificacion].filter(
          (identifier): identifier is string => Boolean(identifier),
        );

        return (
          <div style={{ minWidth: 0 }}>
            <Link
              to={`/clientes/${record.id}`}
              aria-label={t('dashboard.facturacionPorDia.viewClient', { name })}
              style={{ display: 'block' }}
            >
              <Text strong ellipsis={{ tooltip: name }}>
                {name}
              </Text>
            </Link>
            {identifiers.length > 0 && (
              <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                {identifiers.join(' · ')}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: t('dashboard.facturacionPorDia.effectiveDate'),
      dataIndex: 'fechaCobro',
      key: 'fechaCobro',
      width: 205,
      render: (_value: string, record: FacturacionPorDiaCliente) => (
        <div className="facturacion-por-dia__period">
          <Text style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatEffectiveDate(record.siguientePeriodo.fechaInicio) ??
              formatEffectiveDate(record.fechaCobro) ??
              t('common.notAvailable')}
          </Text>
          <Text type="secondary" className="facturacion-por-dia__period-end">
            {t('dashboard.facturacionPorDia.periodThrough', {
              date:
                formatEffectiveDate(record.siguientePeriodo.fechaFin) ?? t('common.notAvailable'),
            })}
          </Text>
        </div>
      ),
    },
    {
      title: t('dashboard.facturacionPorDia.configuredDay'),
      dataIndex: 'diaCobro',
      key: 'diaCobro',
      align: 'center',
      width: 140,
      render: (value: number | null) =>
        value === null ? (
          <Text type="secondary">{t('common.notAvailable')}</Text>
        ) : (
          <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Text>
        ),
    },
    {
      title: t('dashboard.facturacionPorDia.latestInvoice'),
      key: 'ultimaFacturaGenerada',
      width: 235,
      render: (_value: unknown, record: FacturacionPorDiaCliente) => (
        <InvoiceStateCell record={record} t={t} />
      ),
    },
    {
      title: t('dashboard.facturacionPorDia.amounts'),
      key: 'amounts',
      width: 245,
      render: (_value: unknown, record: FacturacionPorDiaCliente) => (
        <InvoiceAmountsCell invoice={record.ultimaFacturaGenerada} t={t} />
      ),
    },
    {
      title: t('dashboard.facturacionPorDia.applications'),
      key: 'applications',
      width: 270,
      render: (_value: unknown, record: FacturacionPorDiaCliente) => (
        <ApplicationsCell invoice={record.ultimaFacturaGenerada} language={language} t={t} />
      ),
    },
    {
      title: t('dashboard.facturacionPorDia.recentCoverage'),
      key: 'recentCoverage',
      width: 310,
      render: (_value: unknown, record: FacturacionPorDiaCliente) => (
        <CoverageIndicator periods={record.coberturaPeriodos} language={language} t={t} />
      ),
    },
  ];

  const controls = (
    <div
      className="facturacion-por-dia__controls"
      aria-label={t('dashboard.facturacionPorDia.controlsLabel')}
    >
      <label htmlFor="dashboard-facturacion-por-dia-day">
        {t('dashboard.facturacionPorDia.dayLabel')}
      </label>
      <Select<number>
        id="dashboard-facturacion-por-dia-day"
        aria-label={t('dashboard.facturacionPorDia.dayLabel')}
        value={selectedDay}
        options={BILLING_DAYS.map((day) => ({
          value: day,
          label: t('dashboard.facturacionPorDia.dayOption', { day }),
        }))}
        virtual={false}
        loading={query.isFetching}
        onChange={setSelectedDay}
        style={{ width: 132 }}
      />
      <Button
        icon={<ReloadOutlined />}
        loading={query.isRefetching}
        onClick={() => void query.refetch()}
      >
        {t('dashboard.facturacionPorDia.refresh')}
      </Button>
    </div>
  );

  let content: ReactNode;
  if (initialLoading) {
    content = <Skeleton active paragraph={{ rows: 7 }} />;
  } else if (query.isError && !response) {
    content = (
      <EmptyState
        title={t('dashboard.facturacionPorDia.errorTitle')}
        description={t('dashboard.facturacionPorDia.errorDescription')}
        action={<Button onClick={() => void query.refetch()}>{t('common.retry')}</Button>}
      />
    );
  } else {
    content = (
      <>
        {query.isError && response && (
          <Alert
            type="warning"
            showIcon
            message={t('dashboard.facturacionPorDia.refreshError')}
            style={{ marginBottom: 16 }}
          />
        )}
        <div className="facturacion-por-dia__summary">
          <Text type="secondary">
            {t('dashboard.facturacionPorDia.summary', {
              count: entries.length,
              day: selectedDay,
            })}
          </Text>
          <Text type="secondary" className="facturacion-por-dia__date-help">
            {t('dashboard.facturacionPorDia.effectiveDateHelp')}
          </Text>
        </div>
        <div className="facturacion-por-dia__operational-help">
          <Text type="secondary">{t('dashboard.facturacionPorDia.coverageSettlementHelp')}</Text>
        </div>
        {entries.length === 0 ? (
          <EmptyState
            compact
            title={t('dashboard.facturacionPorDia.emptyTitle')}
            description={t('dashboard.facturacionPorDia.emptyDescription')}
          />
        ) : (
          <Table<FacturacionPorDiaCliente>
            className="facturacion-por-dia__table"
            dataSource={entries}
            columns={columns}
            rowKey="id"
            pagination={false}
            size="middle"
            scroll={{ x: 1680 }}
          />
        )}
      </>
    );
  }

  return (
    <div data-testid="facturacion-por-dia-view">
      <DashboardCard
        title={t('dashboard.facturacionPorDia.title')}
        subtitle={t('dashboard.facturacionPorDia.subtitle')}
        headerClassName="facturacion-por-dia__header"
        extra={controls}
        bodyStyle={{ padding: initialLoading ? '20px 22px' : '4px 4px 12px' }}
      >
        {content}
      </DashboardCard>
    </div>
  );
}
